import { useState } from 'react';
import { AlertCircle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import { Button, Card, CardHeader, ErrorState, StatusBadge, TextSkeleton, cn, type Tone } from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { formatDateTime } from '../../../lib/format';

/**
 * Settings → System status and Integrations (owner, 29 Oct 2026): is the
 * API up and when was it down, and is every outside service it uses working.
 * The figures come from `GET /admin/system/status`, which checks live.
 */

type State = 'ok' | 'down' | 'not_configured' | 'configured';
interface Service {
  key: string; name: string; purpose: string; provider: string | null;
  state: State; detail: string; latencyMs: number | null;
}
interface Status {
  checkedAt: string;
  uptime: {
    runningSince: string; uptimeSeconds: number; host: string; version: string | null;
    recordedSince: string; days: number; uptimePercent: number; restarts: number;
    outages: Array<{ from: string; to: string; minutes: number }>;
  };
  services: Service[];
}

const STATE: Record<State, { label: string; tone: Tone }> = {
  ok: { label: 'Working', tone: 'covered' },
  down: { label: 'Not working', tone: 'overdue' },
  not_configured: { label: 'Not set up', tone: 'dueSoon' },
  configured: { label: 'Key set', tone: 'neutral' },
};

function useStatus() {
  return useQuery<Status>(() => api.get('/admin/system/status?days=7'), []);
}

function Refresh({ loading, onClick, at }: { loading: boolean; onClick: () => void; at?: string }) {
  return (
    <div className="flex items-center gap-3">
      {at && <span className="hidden text-caption text-neutral-500 sm:inline">Checked {formatDateTime(at)}</span>}
      <Button variant="secondary" loading={loading} onClick={onClick}>
        <RefreshCw className="h-4 w-4" aria-hidden /> Check again
      </Button>
    </div>
  );
}

interface Slot { at: string; state: State | 'none'; detail: string | null; latencyMs: number | null }
interface HistoryRow {
  key: string; name: string; purpose: string; provider: string | null;
  state: State; detail: string; slots: Slot[]; uptimePercent: number | null;
}
interface History {
  hours: number; slotMinutes: number; from: string; to: string;
  services: HistoryRow[];
  problems: Array<{ at: string; service: string; detail: string | null; latencyMs: number | null }>;
}

const BAR: Record<Slot['state'], string> = {
  ok: 'bg-success-500',
  configured: 'bg-success-500/45',
  not_configured: 'bg-[#E9B949]',
  down: 'bg-danger-600',
  none: 'bg-neutral-200',
};
const SLOT_LABEL: Record<Slot['state'], string> = {
  ok: 'Working', configured: 'Key set (not tested live)', not_configured: 'Not set up', down: 'Not working', none: 'No check recorded',
};
const RANGES = [
  { hours: 12, label: '12 hours' },
  { hours: 24, label: '24 hours' },
  { hours: 168, label: '7 days' },
];

function rowIcon(r: HistoryRow) {
  const down = r.state === 'down';
  const unset = r.state === 'not_configured';
  const Icon = down ? XCircle : unset ? AlertCircle : CheckCircle2;
  return <Icon className={cn('h-5 w-5 shrink-0', down ? 'text-danger-600' : unset ? 'text-warning-600' : 'text-success-600')} aria-hidden />;
}

/**
 * Like a public status page: a bar per service, one slot per five minutes
 * (hourly over a week), green when it worked. Tap a slot for what happened
 * then; every failure is listed below with its error, to trace which API
 * failed and why.
 */
export function SystemStatusPanel() {
  const [hours, setHours] = useState(12);
  const history = useQuery<History>(() => api.get(`/admin/system/history?hours=${hours}`), [hours]);
  const uptime = useStatus();
  const [picked, setPicked] = useState<{ service: string; index: number } | null>(null);

  if (history.error) return <ErrorState title="Could not read the system status" detail={history.error} onRetry={history.reload} />;
  const h = history.data;
  const names = new Map((h?.services ?? []).map((x) => [x.key, x.name] as const));
  const down = (h?.services ?? []).filter((x) => x.state === 'down');
  const unset = (h?.services ?? []).filter((x) => x.state === 'not_configured');
  const reload = () => { history.reload(); uptime.reload(); };
  const span = hours >= 168 ? '7 days ago' : `${hours} hours ago`;

  return (
    <div className="space-y-4">
      {h && (
        <div className={cn('rounded-card border', down.length ? 'border-danger-600/40' : 'border-success-500/50')}>
          <div className={cn('flex items-center gap-2 rounded-t-card px-4 py-3 font-medium', down.length ? 'bg-danger-100 text-danger-600' : 'bg-success-100 text-success-600')}>
            {down.length ? <XCircle className="h-5 w-5" aria-hidden /> : <CheckCircle2 className="h-5 w-5" aria-hidden />}
            {down.length ? `${down.map((x) => x.name).join(', ')} ${down.length === 1 ? 'is' : 'are'} not working` : 'All systems working'}
          </div>
          <div className="bg-white px-4 py-3 text-small text-neutral-700">
            {uptime.data ? `API running since ${formatDateTime(uptime.data.uptime.runningSince)}. ` : ''}
            {unset.length ? `Not set up: ${unset.map((x) => x.name).join(', ')}.` : 'Every service it uses is set up.'}
          </div>
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <div className="flex items-center gap-1">
            <h2 className="mr-2 text-h4 text-neutral-900">System status</h2>
            {RANGES.map((r) => (
              <button
                key={r.hours} type="button" onClick={() => { setHours(r.hours); setPicked(null); }}
                aria-pressed={hours === r.hours}
                className={cn('min-h-9 rounded-btn px-2.5 text-small', hours === r.hours ? 'bg-primary-50 font-medium text-primary-700' : 'text-neutral-600 hover:bg-neutral-50')}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Refresh loading={history.loading} onClick={reload} at={uptime.data?.checkedAt} />
        </div>

        {!h ? <div className="p-4"><TextSkeleton lines={8} /></div> : (
          <ul className="divide-y divide-neutral-100">
            {h.services.map((r) => {
              const sel = picked?.service === r.key ? r.slots[picked.index] : null;
              return (
                <li key={r.key} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      {rowIcon(r)}
                      <span className="whitespace-nowrap font-medium text-neutral-900">{r.name}</span>
                      {r.provider && <span className="hidden truncate text-caption text-neutral-500 sm:inline">{r.provider}</span>}
                    </div>
                    <span className="shrink-0 text-small tabular-nums text-neutral-600">
                      {r.uptimePercent == null ? 'No checks yet' : `${r.uptimePercent}% uptime`}
                    </span>
                  </div>
                  <div className="mt-2 flex h-8 items-stretch gap-px" role="group" aria-label={`${r.name}, one bar per ${h.slotMinutes} minutes`}>
                    {r.slots.map((x, i) => (
                      <button
                        key={x.at} type="button"
                        title={`${formatDateTime(x.at)} — ${SLOT_LABEL[x.state]}${x.detail && x.state === 'down' ? `: ${x.detail}` : ''}`}
                        aria-label={`${formatDateTime(x.at)}: ${SLOT_LABEL[x.state]}`}
                        onClick={() => setPicked(picked?.service === r.key && picked.index === i ? null : { service: r.key, index: i })}
                        className={cn('min-w-0 flex-1 rounded-[2px] transition-opacity hover:opacity-70', BAR[x.state],
                          picked?.service === r.key && picked.index === i && 'ring-2 ring-primary-500 ring-offset-1')}
                      />
                    ))}
                  </div>
                  <div className="mt-1 flex justify-between text-micro text-neutral-500">
                    <span>{span}</span><span>Now</span>
                  </div>
                  {sel && (
                    <div className={cn('mt-2 rounded-input border px-3 py-2 text-small', sel.state === 'down' ? 'border-danger-600/30 bg-danger-100/60' : 'border-neutral-200 bg-neutral-50')}>
                      <div className="font-medium text-neutral-900">
                        {formatDateTime(sel.at)} – {formatDateTime(new Date(new Date(sel.at).getTime() + h.slotMinutes * 60_000).toISOString())} · {SLOT_LABEL[sel.state]}
                      </div>
                      {sel.detail && <div className={sel.state === 'down' ? 'text-danger-600' : 'text-neutral-700'}>{sel.detail}</div>}
                      {sel.latencyMs != null && <div className="text-caption text-neutral-500">Answered in {sel.latencyMs} ms</div>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-neutral-100 px-4 py-2 text-caption text-neutral-600">
          {(['ok', 'configured', 'not_configured', 'down', 'none'] as const).map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5"><span className={cn('h-3 w-2 rounded-[2px]', BAR[k])} aria-hidden />{SLOT_LABEL[k]}</span>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="Recent problems" hint="Every failed check in the chosen window, newest first, with the error each service gave." />
        <div className="px-4 pb-4">
          {!h ? <TextSkeleton lines={3} /> : h.problems.length === 0 ? (
            <p className="py-3 text-small text-neutral-600">No errors in the last {hours >= 168 ? '7 days' : `${hours} hours`}.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {h.problems.map((p, i) => (
                <li key={`${p.at}-${p.service}-${i}`} className="grid gap-1 py-2.5 text-small sm:grid-cols-[11rem_9rem_minmax(0,1fr)] sm:gap-3">
                  <span className="tabular-nums text-neutral-600">{formatDateTime(p.at)}</span>
                  <span className="font-medium text-neutral-900">{names.get(p.service) ?? p.service}</span>
                  <span className="min-w-0 break-words text-danger-600">{p.detail ?? 'Failed'}{p.latencyMs != null ? <span className="text-neutral-500"> · {p.latencyMs} ms</span> : null}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}

/** Every outside service the platform uses: which provider, and is it working. */
export function IntegrationsPanel() {
  const status = useStatus();
  if (status.error) return <ErrorState title="Could not check the services" detail={status.error} onRetry={status.reload} />;
  const s = status.data;
  return (
    <Card>
      <CardHeader
        title="Services and APIs"
        hint="Checked live each time this opens. SMS, GST and maps are not tried, because a test would send a message or be billed; for those it shows whether a key is set."
        actions={<Refresh loading={status.loading} onClick={status.reload} at={s?.checkedAt} />}
      />
      {!s ? <div className="p-4"><TextSkeleton lines={5} /></div> : (
        <ul className="grid gap-3 px-4 pb-4 md:grid-cols-2">
          {s.services.map((x) => (
            <li key={x.key} className="rounded-card border border-neutral-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-neutral-900">{x.name}</div>
                  <div className="text-caption text-neutral-500">{x.purpose}</div>
                </div>
                <StatusBadge tone={STATE[x.state].tone}>{STATE[x.state].label}</StatusBadge>
              </div>
              <dl className="mt-3 space-y-1 text-small">
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-neutral-500">Provider</dt><dd className="min-w-0 break-words text-neutral-800">{x.provider ?? '—'}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-neutral-500">Status</dt><dd className={x.state === 'down' ? 'min-w-0 break-words text-danger-700' : 'min-w-0 break-words text-neutral-800'}>{x.detail}</dd></div>
                {x.latencyMs != null && <div className="flex gap-2"><dt className="w-20 shrink-0 text-neutral-500">Answered in</dt><dd className="tabular-nums text-neutral-800">{x.latencyMs} ms</dd></div>}
              </dl>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
